import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Modal,
  TextField,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useParams, useNavigate, Navigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  useGetStocktakeBundleQuery,
  useUpdateStocktakeLineMutation,
  useScanStocktakeCodeMutation,
  useSearchStocktakeProductsMutation,
  useApplyStocktakeProductMutation,
  useCompleteStocktakeMutation,
  useReopenStocktakeMutation,
  useDeleteStocktakeLineMutation,
  useMarkStocktakeDeletedMutation,
  useAddStocktakeLineMutation,
} from '../../store/api/stocktakeApi';
import { useAuth } from '../../hooks/useAuth';
import { useDesktopModalInput } from '../../hooks/useDesktopModalInput';
import { useBarcodeScannerWaiting } from '../../hooks/useBarcodeScannerWaiting';
import routes from '../../routes/routes';
import ProductImage from '../ui/ProductImage';
import BarcodeCameraDialog from '../picking/BarcodeCameraDialog';
import PickingScanErrorDialog from '../picking/PickingScanErrorDialog';
import StocktakeLineCard from './StocktakeLineCard';
import StocktakeCompareDot from './StocktakeCompareDot';
import {
  getCompareStatus,
  getLineReferenceQty,
  getLineReferenceLabel,
  lineMatchesStatusFilter,
  LINE_STATUS_FILTER,
} from '../../helpers/stocktakeLineCompare';
import ProductPickerDialog from './ProductPickerDialog';
import StocktakeProductPickDialog from './StocktakeProductPickDialog';
import StocktakeQuantityEditDialog from './StocktakeQuantityEditDialog';
import StocktakeCalcAddDialog from './StocktakeCalcAddDialog';
import {
  TERMINAL_ALPHANUM_KEYBOARD_LAYOUT,
  TERMINAL_ALPHANUM_KEYBOARD_DISPLAY,
} from '../../config/terminalKeyboardLayouts';
import { terminalColors, terminalBarIconButtonSx, documentCardSx, terminalSelectSx, terminalSelectMenuProps } from '../../theme/terminalStyles';
import MarkDeletedDialog, { formatDeletedDocumentLabel } from '../ui/MarkDeletedDialog';
import { config } from '../../config/config';

const DETAIL_DOT_SIZE = 40;

const lineFilterFormControlSx = {
  ...terminalSelectSx,
  flexShrink: 0,
  minWidth: { xs: 148, sm: 168 },
  '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
  '& .MuiSelect-select': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    py: 0.75,
    fontWeight: 500,
  },
};

const formatArticle = (product) => {
  const a = product?.article;
  if (a == null || String(a).trim() === '') return '—';
  return String(a).trim();
};

const StocktakeWorkspacePage = () => {
  const { stocktakeId } = useParams();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const sid = Number(stocktakeId);
  const { data: bundle, isLoading, error } = useGetStocktakeBundleQuery(sid, { skip: !sid });
  const [updateQty, { isLoading: isSavingLine }] = useUpdateStocktakeLineMutation();
  const [scanCode] = useScanStocktakeCodeMutation();
  const [searchProducts] = useSearchStocktakeProductsMutation();
  const [applyProduct] = useApplyStocktakeProductMutation();
  const [completeStocktake, { isLoading: completing }] = useCompleteStocktakeMutation();
  const [reopenStocktake, { isLoading: reopening }] = useReopenStocktakeMutation();
  const [deleteLine, { isLoading: deletingLine }] = useDeleteStocktakeLineMutation();
  const [markDeleted, { isLoading: markingDeleted }] = useMarkStocktakeDeletedMutation();
  const [addLine] = useAddStocktakeLineMutation();

  const [sessionStarted, setSessionStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState('items');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [detailLineId, setDetailLineId] = useState(null);
  const [toast, setToast] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [spotlightLineId, setSpotlightLineId] = useState(null);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [deleteLineTarget, setDeleteLineTarget] = useState(null);
  const [deleteDocOpen, setDeleteDocOpen] = useState(false);
  const [deleteDocError, setDeleteDocError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [productPickOpen, setProductPickOpen] = useState(false);
  const [productPickItems, setProductPickItems] = useState([]);
  const [qtyEditOpen, setQtyEditOpen] = useState(false);
  const [lineStatusFilter, setLineStatusFilter] = useState(LINE_STATUS_FILTER.ALL);
  const [scanErrorModal, setScanErrorModal] = useState(null);
  const [enterQty, setEnterQty] = useState(() => Boolean(user?.terminalWorkplace?.showCalcDialogST));
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcSuggested, setCalcSuggested] = useState(1);
  const [pendingCalcLineId, setPendingCalcLineId] = useState(null);

  const detailCloseTimerRef = useRef(null);
  const manualKeyboardRef = useRef(null);
  const spotlightTimerRef = useRef(null);
  const enterQtyRef = useRef(enterQty);
  enterQtyRef.current = enterQty;

  const fromERP = Boolean(bundle?.fromERP);
  const isDone = Boolean(bundle?.isDone);

  const lines = useMemo(() => {
    return [...(bundle?.products || [])].sort((a, b) => a.id - b.id);
  }, [bundle]);

  const filteredLines = useMemo(() => {
    if (lineStatusFilter === LINE_STATUS_FILTER.ALL) return lines;
    return lines.filter((line) => lineMatchesStatusFilter(line, fromERP, lineStatusFilter));
  }, [lines, lineStatusFilter, fromERP]);

  const detailLine = useMemo(() => {
    if (!detailLineId) return null;
    return lines.find((l) => l.id === detailLineId) || null;
  }, [lines, detailLineId]);

  const editEnabled = sessionStarted && !isPaused && !isDone;

  const scanEnabled = editEnabled && !completeConfirmOpen && !calcOpen;

  const pulseLineOutline = useCallback((lineId) => {
    setSpotlightLineId(lineId);
    if (spotlightTimerRef.current) window.clearTimeout(spotlightTimerRef.current);
    spotlightTimerRef.current = window.setTimeout(() => setSpotlightLineId(null), 2700);
  }, []);

  const openLineDetail = useCallback((lineId) => {
    if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
    setDetailLineId(lineId);
  }, []);

  const applyScanResult = useCallback(
    (stocktakeProduct) => {
      if (!stocktakeProduct?.id) return;
      pulseLineOutline(stocktakeProduct.id);
      setWorkspaceTab('items');
      openLineDetail(stocktakeProduct.id);
    },
    [pulseLineOutline, openLineDetail]
  );

  const queueCalcForLine = useCallback((lineId, suggestedAdd) => {
    const add = Number(suggestedAdd);
    setCalcSuggested(Number.isFinite(add) ? add : 1);
    setPendingCalcLineId(lineId);
  }, []);

  const showApplyError = useCallback(
    (e, productHint) => {
      const msg = e?.data?.message || e?.message || 'Помилка';
      if (fromERP && msg.includes('відсутній')) {
        setScanErrorModal({
          variant: 'not_in_document',
          product: productHint || { name: '—' },
        });
        return;
      }
      if (msg.includes('не знайдено')) {
        setToast({ severity: 'warning', message: msg });
        return;
      }
      setToast({ severity: 'error', message: msg });
    },
    [fromERP]
  );

  const applyProductResult = useCallback(
    async (productId, productHint) => {
      try {
        const res = await applyProduct({
          stocktakeId: sid,
          productId,
          increment: !enterQtyRef.current,
        }).unwrap();
        applyScanResult(res.stocktakeProduct);
        if (enterQtyRef.current && res.stocktakeProduct?.id) {
          const suggested = Number(res.suggestedAdd);
          const fallback = Number(res.stocktakeProduct?.product?.multiplicity ?? 1) || 1;
          queueCalcForLine(res.stocktakeProduct.id, Number.isFinite(suggested) ? suggested : fallback);
        }
      } catch (e) {
        showApplyError(e, productHint);
      }
    },
    [applyProduct, sid, applyScanResult, showApplyError, queueCalcForLine]
  );

  const handleScanCode = useCallback(
    async (rawCode) => {
      if (!scanEnabled || !bundle) return;
      const code = String(rawCode || '').trim();
      if (!code) return;

      try {
        const res = await scanCode({
          stocktakeId: sid,
          code,
          increment: !enterQtyRef.current,
        }).unwrap();
        applyScanResult(res.stocktakeProduct);
        if (enterQtyRef.current && res.stocktakeProduct?.id) {
          const suggested = Number(res.suggestedAdd);
          const fallback = Number(res.stocktakeProduct?.product?.multiplicity ?? 1) || 1;
          queueCalcForLine(res.stocktakeProduct.id, Number.isFinite(suggested) ? suggested : fallback);
        }
      } catch (e) {
        const msg = e?.data?.message || e?.message || 'Помилка сканування';
        if (fromERP && msg.includes('відсутній')) {
          setScanErrorModal({
            variant: 'not_in_document',
            product: { name: code },
          });
          return;
        }
        if (msg.includes('не знайдено')) {
          setToast({ severity: 'warning', message: msg });
          return;
        }
        setToast({ severity: 'error', message: msg });
      }
    },
    [scanEnabled, bundle, scanCode, sid, applyScanResult, fromERP, queueCalcForLine]
  );

  const handleManualSearch = useCallback(
    async (rawCode) => {
      if (!scanEnabled || !bundle) return;
      const code = String(rawCode || '').trim();
      if (!code) return;

      try {
        const res = await searchProducts({ stocktakeId: sid, code }).unwrap();
        const items = res.products || [];
        if (!items.length) {
          setToast({ severity: 'warning', message: 'Товар не знайдено' });
          return;
        }
        if (items.length === 1) {
          const p = items[0];
          await applyProductResult(p.id, {
            article: p.article,
            name: `${p.name || ''} ${p.modName || ''}`.trim(),
          });
          return;
        }
        setProductPickItems(items);
        setProductPickOpen(true);
      } catch (e) {
        showApplyError(e);
      }
    },
    [scanEnabled, bundle, searchProducts, sid, applyProductResult, showApplyError]
  );

  const handleProductPickSelect = (product) => {
    setProductPickOpen(false);
    setProductPickItems([]);
    void applyProductResult(product.id, {
      article: product.article,
      name: `${product.name || ''} ${product.modName || ''}`.trim(),
    });
  };

  const applyManualModal = useCallback(() => {
    handleManualSearch(manualCode);
    setManualCode('');
    manualKeyboardRef.current?.clearInput();
    setManualOpen(false);
  }, [handleManualSearch, manualCode]);

  const { inputRef: manualInputRef, handleInputKeyDown: manualInputKeyDown } = useDesktopModalInput({
    open: manualOpen,
    onConfirm: applyManualModal,
    enabled: scanEnabled,
    focusKey: manualOpen,
  });

  useBarcodeScannerWaiting(
    config.scanCodeLength,
    scanEnabled && Boolean(bundle && !isDone),
    [scanEnabled, bundle, isDone, completeConfirmOpen],
    { mthd: handleScanCode }
  );

  useEffect(() => {
    if (pendingCalcLineId == null) return;
    const line = lines.find((l) => l.id === pendingCalcLineId);
    if (!line) return;
    setCalcOpen(true);
    setPendingCalcLineId(null);
  }, [pendingCalcLineId, lines]);

  useEffect(() => {
    if (!detailLineId) setCalcOpen(false);
  }, [detailLineId]);

  useEffect(
    () => () => {
      if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
      if (spotlightTimerRef.current) window.clearTimeout(spotlightTimerRef.current);
    },
    []
  );

  useEffect(() => {
    setSessionStarted(false);
    setIsPaused(false);
    setDetailLineId(null);
    setWorkspaceTab('items');
    setLineStatusFilter(LINE_STATUS_FILTER.ALL);
    setEnterQty(Boolean(user?.terminalWorkplace?.showCalcDialogST));
    setCalcOpen(false);
    setPendingCalcLineId(null);
  }, [sid, user?.terminalWorkplace?.showCalcDialogST]);

  if (!user?.terminalWorkplace?.isStockChecking) {
    return <Navigate to={routes.root} replace />;
  }

  if (isLoading) {
    return (
      <TerminalLayout
        title="Інвентаризація"
        titleIcon={<FactCheckOutlinedIcon />}
        backTo={routes.stocktake}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </TerminalLayout>
    );
  }

  if (error || !bundle) {
    return (
      <TerminalLayout
        title="Інвентаризація"
        titleIcon={<FactCheckOutlinedIcon />}
        backTo={routes.stocktake}
      >
        <Typography color="error" sx={{ mt: 2 }}>
          Документ не знайдено. Поверніться до списку.
        </Typography>
        <TerminalOrangeButton sx={{ mt: 2 }} onClick={() => navigate(routes.stocktake)}>
          До списку
        </TerminalOrangeButton>
      </TerminalLayout>
    );
  }

  const changeQtyBy = async (line, deltaMult) => {
    if (!editEnabled || !line) return;
    const mult = Number(line.product?.multiplicity ?? 1) || 1;
    const cur = Number(line.quantityActual ?? 0);
    const step = mult * deltaMult;
    let next = Math.max(0, cur + step);
    try {
      await updateQty({
        stocktakeId: sid,
        lineId: line.id,
        quantity: next,
      }).unwrap();
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleQtyEditConfirm = async (raw) => {
    if (!editEnabled || !detailLine) return;
    const next = Number(String(raw ?? '').trim().replace(',', '.'));
    if (Number.isNaN(next) || next < 0) {
      setToast({ severity: 'warning', message: 'Некоректна кількість' });
      return;
    }
    try {
      await updateQty({
        stocktakeId: sid,
        lineId: detailLine.id,
        quantity: next,
      }).unwrap();
      setQtyEditOpen(false);
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleCalcConfirm = async (delta) => {
    if (!editEnabled || !detailLine) return;
    const add = Number(delta);
    if (!Number.isFinite(add)) {
      setToast({ severity: 'warning', message: 'Некоректна кількість' });
      return;
    }
    const cur = Number(detailLine.quantityActual ?? 0);
    const next = Math.max(0, Math.round((cur + add) * 1e6) / 1e6);
    try {
      await updateQty({
        stocktakeId: sid,
        lineId: detailLine.id,
        quantity: next,
      }).unwrap();
      setCalcOpen(false);
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleComplete = async () => {
    try {
      await completeStocktake(sid).unwrap();
      setCompleteConfirmOpen(false);
      setSessionStarted(false);
      setToast({ severity: 'success', message: 'Інвентаризацію завершено' });
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleReopen = async () => {
    try {
      await reopenStocktake(sid).unwrap();
      setReopenConfirmOpen(false);
      setSessionStarted(false);
      setIsPaused(false);
      setToast({ severity: 'success', message: 'Режим редагування доступний' });
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteLineTarget) return;
    try {
      await deleteLine({ stocktakeId: sid, lineId: deleteLineTarget.id }).unwrap();
      if (detailLineId === deleteLineTarget.id) setDetailLineId(null);
      setDeleteLineTarget(null);
      setToast({ severity: 'success', message: 'Рядок видалено' });
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка видалення',
      });
    }
  };

  const handlePickerSelect = async (product) => {
    setPickerOpen(false);
    try {
      const mult = Number(product.multiplicity ?? 1) || 1;
      const askQty = enterQtyRef.current;
      const res = await addLine({
        stocktakeId: sid,
        productId: product.id,
        quantityActual: askQty ? 0 : mult,
      }).unwrap();
      applyScanResult(res.stocktakeProduct);
      if (askQty && res.stocktakeProduct?.id) {
        queueCalcForLine(res.stocktakeProduct.id, mult);
      }
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка додавання',
      });
    }
  };

  const togglePlayPauseLock = () => {
    if (isDone) {
      setReopenConfirmOpen(true);
      return;
    }
    if (!sessionStarted) {
      setSessionStarted(true);
      setIsPaused(false);
      window.requestAnimationFrame(() => document.activeElement?.blur?.());
    } else {
      setIsPaused((p) => !p);
    }
  };

  const titleLabel = fromERP
    ? `№ ${bundle.number || bundle.id}`
    : `# ${bundle.id}`;

  const docDate =
    bundle.date != null
      ? new Date(bundle.date).toLocaleDateString('uk-UA', { dateStyle: 'short' })
      : '—';

  const commentText = bundle.comment?.trim?.() ? String(bundle.comment).trim() : '';

  const deleteDocLabel = formatDeletedDocumentLabel({
    number: fromERP ? bundle.number || bundle.id : bundle.id,
    date: bundle.date,
    useHash: !fromERP,
  });

  const handleConfirmDeleteDoc = async () => {
    setDeleteDocError('');
    try {
      await markDeleted(sid).unwrap();
      navigate(routes.stocktake);
    } catch (e) {
      setDeleteDocError(e?.data?.message || 'Не вдалося помітити документ на видалення');
    }
  };

  const headerEndActions = (
    <>
      <IconButton
        aria-label="помітити на видалення"
        onClick={() => {
          setDeleteDocError('');
          setDeleteDocOpen(true);
        }}
        sx={{
          ...terminalBarIconButtonSx,
          bgcolor: 'error.main',
          color: '#fff',
          '&:hover': { bgcolor: 'error.dark' },
        }}
      >
        <DeleteOutlineIcon />
      </IconButton>
      {isDone ? (
        <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} aria-hidden />
      ) : (
        <RadioButtonUncheckedIcon sx={{ color: terminalColors.accentOrange, fontSize: 28 }} aria-hidden />
      )}
    </>
  );

  const bottomBar = (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: '0 0 auto', minWidth: 0 }}>
        <IconButton
          aria-label={isDone ? 'завершено' : sessionStarted ? 'пауза' : 'старт'}
          onClick={togglePlayPauseLock}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
          }}
          sx={terminalBarIconButtonSx}
        >
          {isDone ? (
            <LockOutlinedIcon />
          ) : !sessionStarted || isPaused ? (
            <PlayArrowIcon />
          ) : (
            <PauseIcon />
          )}
        </IconButton>
        <IconButton
          aria-label="штрихкод"
          disabled={!scanEnabled}
          onClick={() => setManualOpen(true)}
          sx={terminalBarIconButtonSx}
        >
          <KeyboardIcon />
        </IconButton>
        <IconButton
          disabled={!scanEnabled}
          aria-label="камера"
          onClick={() => setCameraOpen(true)}
          sx={terminalBarIconButtonSx}
        >
          <PhotoCameraIcon />
        </IconButton>
        {!fromERP && editEnabled ? (
          <IconButton
            aria-label="додати товар"
            onClick={() => setPickerOpen(true)}
            sx={terminalBarIconButtonSx}
          >
            <AddIcon />
          </IconButton>
        ) : null}
      </Box>
      {editEnabled ? (
        <FormControlLabel
          sx={{
            m: 0,
            ml: 'auto',
            flex: '0 1 auto',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            '& .MuiCheckbox-root': { p: 0.25, flexShrink: 0 },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.68rem',
              lineHeight: 1.05,
              fontWeight: 600,
              color: terminalColors.textPrimary,
              whiteSpace: 'normal',
            },
          }}
          control={
            <Checkbox
              size="small"
              checked={enterQty}
              onChange={(e) => setEnterQty(e.target.checked)}
              inputProps={{ 'aria-label': 'Вводити кількість' }}
              sx={{
                color: terminalColors.textPrimary,
                '&.Mui-checked': { color: terminalColors.textPrimary },
              }}
            />
          }
          label="Вводити кількість"
        />
      ) : null}
      {editEnabled ? (
        <TerminalOrangeButton
          size="small"
          onClick={() => setCompleteConfirmOpen(true)}
          sx={{ flexShrink: 0, minWidth: 'auto', px: 1.5, py: 0.75, fontSize: '0.8rem' }}
        >
          Завершити
        </TerminalOrangeButton>
      ) : null}
    </Box>
  );

  const detailFact = Number(detailLine?.quantityActual ?? 0);
  const detailDoc = detailLine ? getLineReferenceQty(detailLine, fromERP) : 0;
  const detailRefLabel = getLineReferenceLabel(fromERP);
  const detailCompare = detailLine
    ? getCompareStatus(detailDoc, detailLine.quantityActual)
    : null;

  return (
    <TerminalLayout
      title={`Інвентаризація ${titleLabel}`}
      titleIcon={<FactCheckOutlinedIcon />}
      backTo={routes.stocktake}
      endActions={headerEndActions}
      bottomBar={bottomBar}
    >
      {!sessionStarted && !isDone ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {commentText || 'Натисніть «Старт» на нижній панелі, щоб почати інвентаризацію.'}
        </Alert>
      ) : null}
      {isDone ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Інвентаризацію завершено. Переглядайте без змін або відкрийте режим через іконку замка.
        </Alert>
      ) : null}

      <Paper sx={{ mb: 1.5, p: 0.5 }} elevation={0}>
        <ToggleButtonGroup
          exclusive
          value={workspaceTab}
          onChange={(e, v) => {
            if (v) setWorkspaceTab(v);
          }}
          fullWidth
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <ToggleButton value="items">Товари</ToggleButton>
          <ToggleButton value="doc">Документ</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {workspaceTab === 'items' ? (
        <Paper sx={{ ...documentCardSx, mb: 1.5, py: 1, px: 1.5 }} elevation={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
              minWidth: 0,
            }}
          >
            <FormControl size="small" sx={lineFilterFormControlSx}>
              <InputLabel id="stocktake-line-status-filter-label">Статус</InputLabel>
              <Select
                labelId="stocktake-line-status-filter-label"
                label="Статус"
                value={lineStatusFilter}
                onChange={(e) => setLineStatusFilter(e.target.value)}
                MenuProps={terminalSelectMenuProps}
              >
                <MenuItem value={LINE_STATUS_FILTER.ALL}>Всі</MenuItem>
                <MenuItem value={LINE_STATUS_FILTER.UNDER}>Факт недостача</MenuItem>
                <MenuItem value={LINE_STATUS_FILTER.OVER}>Факт перевищує</MenuItem>
                <MenuItem value={LINE_STATUS_FILTER.MATCH}>Факт правильно</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      ) : null}

      {workspaceTab === 'doc' ? (
        <Paper
          sx={{ p: 2, bgcolor: terminalColors.cardWhite, borderRadius: 2 }}
          elevation={0}
        >
          <Typography variant="body2">
            <strong>Номер:</strong>{' '}
            {fromERP ? bundle.number || '—' : `# ${bundle.id}`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Дата:</strong> {docDate}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Склад:</strong> {bundle.warehouse?.name || '—'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Користувач:</strong> {bundle.user?.name || bundle.user?.login || '—'}
          </Typography>
          {!fromERP ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Тип:</strong> локальний документ
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Коментар:</strong>{' '}
            {commentText || '—'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {filteredLines.map((line) => (
            <StocktakeLineCard
              key={line.id}
              line={line}
              fromERP={fromERP}
              isSpotlighted={line.id === spotlightLineId}
              deleteEnabled={editEnabled}
              onDelete={setDeleteLineTarget}
              onOpen={() => openLineDetail(line.id)}
            />
          ))}
          {!filteredLines.length ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              {lines.length
                ? 'За обраним фільтром товарів не знайдено'
                : fromERP
                  ? 'Немає рядків у документі'
                  : 'Додайте товари скануванням або кнопкою +'}
            </Typography>
          ) : null}
        </Box>
      )}

      <Modal open={manualOpen} onClose={() => setManualOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 400,
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Введіть штрихкод / артикул
          </Typography>
          <TextField
            fullWidth
            value={manualCode}
            inputRef={manualInputRef}
            onKeyDown={manualInputKeyDown}
            onChange={(e) => setManualCode(e.target.value)}
            margin="normal"
          />
          <Keyboard
            keyboardRef={(r) => {
              manualKeyboardRef.current = r;
            }}
            onChange={setManualCode}
            layout={TERMINAL_ALPHANUM_KEYBOARD_LAYOUT}
            display={TERMINAL_ALPHANUM_KEYBOARD_DISPLAY}
            onKeyPress={(button) => {
              if (button === '{bksp}') {
                setManualCode((prev) => {
                  const next = prev.slice(0, -1);
                  manualKeyboardRef.current?.setInput(next);
                  return next;
                });
              }
            }}
          />
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<PhotoCameraIcon />}
            disabled={!scanEnabled}
            onClick={() => {
              setManualOpen(false);
              setCameraOpen(true);
            }}
          >
            Сканувати з камери
          </TerminalOrangeButton>
          <TerminalOrangeButton fullWidth sx={{ mt: 2 }} onClick={applyManualModal}>
            OK
          </TerminalOrangeButton>
        </Box>
      </Modal>

      <BarcodeCameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCode={(code) => handleScanCode(code)}
      />

      <StocktakeProductPickDialog
        open={productPickOpen}
        products={productPickItems}
        fromERP={fromERP}
        onClose={() => {
          setProductPickOpen(false);
          setProductPickItems([]);
        }}
        onSelect={handleProductPickSelect}
      />

      <ProductPickerDialog
        open={pickerOpen}
        stocktakeId={sid}
        onClose={() => setPickerOpen(false)}
        onSelectProduct={handlePickerSelect}
      />

      <PickingScanErrorDialog
        open={Boolean(scanErrorModal)}
        variant={scanErrorModal?.variant}
        product={scanErrorModal?.product}
        onClose={() => setScanErrorModal(null)}
      />

      <Dialog
        open={Boolean(detailLine)}
        onClose={() => setDetailLineId(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: terminalColors.gradientTop } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>
            Товар
          </Typography>
          <IconButton
            aria-label="Закрити"
            onClick={() => setDetailLineId(null)}
            sx={{
              bgcolor: terminalColors.accentOrange,
              color: terminalColors.textPrimary,
              '&:hover': { bgcolor: terminalColors.accentOrangeHover },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailLine ? (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                Артикул: <strong>{formatArticle(detailLine.product)}</strong>
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                {`${detailLine.product?.name || ''} ${detailLine.product?.modName || ''}`.trim() ||
                  '—'}
              </Typography>
              <ProductImage
                image={detailLine.product?.imageUrl ?? detailLine.product?.image}
                sx={{ width: '100%', maxHeight: 280, my: 2 }}
                placeholderSx={{ width: '100%', maxHeight: 280, my: 2 }}
              />
              {detailLine ? (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {detailRefLabel}: <strong>{detailDoc}</strong>
                </Typography>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  onClick={() => editEnabled && setQtyEditOpen(true)}
                  sx={{
                    cursor: editEnabled ? 'pointer' : 'default',
                    ...(editEnabled
                      ? {
                          textDecoration: 'underline',
                          textUnderlineOffset: 4,
                          textDecorationColor: terminalColors.accentOrange,
                        }
                      : {}),
                  }}
                >
                  Факт: <strong>{detailFact}</strong>
                </Typography>
                {detailCompare ? (
                  <StocktakeCompareDot status={detailCompare} size={DETAIL_DOT_SIZE} />
                ) : null}
              </Box>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Кратність: {detailLine.product?.multiplicity ?? 1}
              </Typography>
              {editEnabled ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <IconButton
                    aria-label="мінус"
                    disabled={isSavingLine || detailFact <= 0.0001}
                    onClick={() => changeQtyBy(detailLine, -1)}
                    sx={{
                      ...terminalBarIconButtonSx,
                      width: 56,
                      height: 56,
                      bgcolor: terminalColors.accentOrange,
                    }}
                  >
                    <RemoveIcon fontSize="large" />
                  </IconButton>
                  <IconButton
                    aria-label="плюс"
                    disabled={isSavingLine}
                    onClick={() => changeQtyBy(detailLine, 1)}
                    sx={{
                      ...terminalBarIconButtonSx,
                      width: 56,
                      height: 56,
                      bgcolor: terminalColors.accentOrange,
                    }}
                  >
                    <AddIcon fontSize="large" />
                  </IconButton>
                  <IconButton
                    aria-label="калькулятор кількості"
                    disabled={isSavingLine}
                    onClick={() => {
                      const mult = Number(detailLine.product?.multiplicity ?? 1) || 1;
                      setCalcSuggested(mult);
                      setPendingCalcLineId(null);
                      setCalcOpen(true);
                    }}
                    sx={{
                      ...terminalBarIconButtonSx,
                      width: 56,
                      height: 56,
                      bgcolor: terminalColors.accentOrange,
                    }}
                  >
                    <CalculateIcon fontSize="large" />
                  </IconButton>
                </Box>
              ) : null}
              {!fromERP && editEnabled ? (
                <TerminalOrangeButton
                  fullWidth
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteLineTarget(detailLine)}
                  sx={{
                    mt: 3,
                    bgcolor: '#ef5350',
                    color: '#fff',
                    '&:hover': { bgcolor: '#e53935' },
                  }}
                >
                  Видалити рядок
                </TerminalOrangeButton>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 3, pt: 1 }}>
          <TerminalOrangeButton
            fullWidth
            size="large"
            onClick={() => setDetailLineId(null)}
            sx={{ minHeight: 64, fontSize: '1.35rem' }}
          >
            ОК
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <StocktakeQuantityEditDialog
        open={qtyEditOpen}
        initialValue={detailFact}
        onClose={() => setQtyEditOpen(false)}
        onConfirm={handleQtyEditConfirm}
        isSaving={isSavingLine}
      />

      <StocktakeCalcAddDialog
        open={calcOpen && Boolean(detailLine)}
        currentQty={detailFact}
        suggestedAdd={calcSuggested}
        onClose={() => setCalcOpen(false)}
        onConfirm={handleCalcConfirm}
        isSaving={isSavingLine}
      />

      <Dialog open={completeConfirmOpen} onClose={() => setCompleteConfirmOpen(false)}>
        <DialogTitle>Завершити інвентаризацію?</DialogTitle>
        <DialogContent>
          <Typography>Після завершення редагування буде заблоковано.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setCompleteConfirmOpen(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleComplete} disabled={completing}>
            {completing ? <CircularProgress size={22} color="inherit" /> : 'Завершити'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenConfirmOpen} onClose={() => setReopenConfirmOpen(false)}>
        <DialogTitle>Відкрити для редагування?</DialogTitle>
        <DialogContent>
          <Typography>Інвентаризацію буде розблоковано для змін.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setReopenConfirmOpen(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleReopen} disabled={reopening}>
            {reopening ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <MarkDeletedDialog
        open={deleteDocOpen}
        documentLabel={deleteDocLabel}
        loading={markingDeleted}
        error={deleteDocError}
        onClose={() => {
          if (markingDeleted) return;
          setDeleteDocOpen(false);
        }}
        onConfirm={handleConfirmDeleteDoc}
      />

      <Dialog open={Boolean(deleteLineTarget)} onClose={() => setDeleteLineTarget(null)}>
        <DialogTitle>Видалити рядок?</DialogTitle>
        <DialogContent>
          <Typography>Товар буде видалено з документа інвентаризації.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setDeleteLineTarget(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleDeleteConfirm} disabled={deletingLine}>
            {deletingLine ? <CircularProgress size={22} color="inherit" /> : 'Видалити'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </TerminalLayout>
  );
};

export default StocktakeWorkspacePage;
